import { Component, Input, OnInit } from '@angular/core';
import {
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  ReactiveFormsModule,
} from '@angular/forms';
import { NgxSpinnerService } from 'ngx-spinner';
import { WarehouseService } from 'src/app/@pages/warehouse/warehouse.service';
import { InventoryService } from 'src/app/@pages/inventory/inventory.service';
import { ErrorsService } from '../../services/alerts/errors.service';
import { Observable, Subject } from 'rxjs';
import { IWarehouse } from 'src/app/@interfaces/warehouse.interface';
import { IArticles } from 'src/app/@interfaces/article.interface';
import { takeUntil, startWith } from 'rxjs/operators';
import { map } from 'rxjs/internal/operators/map';
import { CookieAuthService } from 'src/app/@shared/storage-variables/cookie-auth.service';
import { AlertsService } from '../../services/alerts/alerts.service';
import { TranslatePipe } from '@ngx-translate/core';
import { MatButtonModule } from '@angular/material/button';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatInputModule } from '@angular/material/input';
import { MatOptionModule } from '@angular/material/core';
import { AsyncPipe } from '@angular/common';
import { MatSelectModule } from '@angular/material/select';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatDialogModule } from '@angular/material/dialog';
import { InventoryArticleWarningComponent } from '@shared-components';

@Component({
  selector: 'app-crud-warehouse-output-and-entry',
  templateUrl: './crud-warehouse-output-and-entry.component.html',
  styleUrls: ['./crud-warehouse-output-and-entry.component.css'],
  imports: [
    MatDialogModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatSelectModule,
    MatOptionModule,
    MatInputModule,
    MatAutocompleteModule,
    MatDatepickerModule,
    MatButtonModule,
    AsyncPipe,
    TranslatePipe,
    InventoryArticleWarningComponent,
  ],
})
export class CrudWarehouseOutputAndEntryComponent implements OnInit {
  dateForm: UntypedFormControl = this.fb.control(
    new Date(),
    Validators.required,
  );
  outputForm: UntypedFormGroup = this.fb.group({
    article: ['', [Validators.required]],
    type: ['', [Validators.required]],
    warehouse: ['', [Validators.required]],
    value: [
      0,
      [Validators.required, Validators.min(0.01), Validators.max(99999999.99)],
    ],
    cost: [0, [Validators.max(99999999.99)]],
    description: ['', [Validators.required, Validators.maxLength(100)]],
  });
  @Input() types: any[] = [];
  warehouseFilter: Observable<IWarehouse[]> | undefined;
  warehouseList: IWarehouse[] = [];
  articleList: IArticles[] = [];
  unsubscribe$ = new Subject<void>();
  user = '';
  constructor(
    private fb: UntypedFormBuilder,
    private warehouseService: WarehouseService,
    private spinnerService: NgxSpinnerService,
    private alertsService: AlertsService,
    private inventoryService: InventoryService,
    private errorsService: ErrorsService,
    private cookieAuthService: CookieAuthService,
  ) {}

  async ngOnInit() {
    this.getAuthValues();
    this.data();
    this.filterInputs();
  }

  getAuthValues() {
    this.user = this.cookieAuthService.getUserId!;
  }

  data() {
    this.warehouseService
      .listWarehouses()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((result) => {
        this.warehouseList = result;
      });
    this.findArticles();
  }

  findArticles() {
    this.outputForm.get('article')?.valueChanges.subscribe((result) => {
      if (!result.__typename) {
        this.inventoryService
          .searchArticle(result, null, null, null)
          .pipe(takeUntil(this.unsubscribe$))
          .subscribe((result) => {
            this.articleList = result.articles;
          });
      } else {
        this.outputForm.get('cost')?.setValue(result.cumulative_value);
      }
    });
  }

  filterInputs() {
    this.warehouseFilter = this.outputForm.get('warehouse')?.valueChanges.pipe(
      startWith(''),
      map((value) => this._filterP(value)),
    );
  }

  _filterP(value: string): IWarehouse[] {
    const filterValueP = value.toString().toLowerCase();
    return this.warehouseList.filter((option) =>
      option.nombre.toLowerCase().toString().includes(filterValueP),
    );
  }

  displayFnWarehouse(warehouse: any) {
    return warehouse && warehouse ? warehouse.nombre : undefined;
  }

  displayFnArticle(article: any) {
    return article && article ? article.descripcion : undefined;
  }

  displayFnType(type: any) {
    return type && type ? type.value : undefined;
  }

  register() {
    this.spinnerService.show();
    if (this.outputForm.invalid === true) {
      this.spinnerService.hide();
      this.alertsService.showErrorAlert(
        'Please, fill in all the required fields!',
      );
      return;
    } else {
      const date = this.dateForm.value;
      this.warehouseService
        .createWarehouseOutputOrEntry(
          parseInt(this.outputForm.value.article.id_articulo),
          parseInt(this.outputForm.value.type.id),
          parseInt(this.outputForm.value.warehouse.id_almacen),
          new Date(date).toDateString(),
          parseFloat(this.outputForm.value.value),
          this.outputForm.value.type.id === 2
            ? parseFloat(this.outputForm.value.cost)
            : 0,
          this.outputForm.value.description.toLocaleUpperCase(),
          parseInt(this.user!),
        )
        .pipe(takeUntil(this.unsubscribe$))
        .subscribe(
          (result) => {
            this.afterCreated();
          },
          (error) => {
            this.onCreatedError(error);
          },
        );
    }
  }

  afterCreated() {
    this.spinnerService.hide();
    this.alertsService
      .showSuccessfullAlert('Movement Created')
      .then((result) => {
        if (result.isConfirmed) {
          this.alertsService.refresh('/table', '/inventory_movements');
        }
      });
  }

  onCreatedError(error: any) {
    const errorTransformed = this.errorsService.getErrorFromJson(error);
    this.spinnerService.hide();
    this.alertsService.showErrorAlert(
      errorTransformed.message.split(':')[1].trim(),
    );
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
